import type {
  CombineEntry,
  CombineMove,
  CombinePageRef,
  CombinePlan,
  CombineRow,
} from "./types";

// Everything the combined-set review page computes, kept out of the component so
// it can be tested without rendering anything.
//
// The rule this file exists to hold: the page the operator READS and the page
// list the build RECEIVES are derived from the same array, in the same order, by
// the code below. Two paths — one for display, one for the request — is how a
// list that looks right builds something else.

/** The rows that can be in the file, in the proposed order. */
export function toRows(plan: CombinePlan | null | undefined): CombineRow[] {
  return (plan?.entries ?? [])
    .filter((e) => e.source !== "None")
    .map((e) => ({ ...e, included: e.role !== "Removed", useApproved: false }));
}

/** Can the operator keep the approved version of this sheet instead?
 *
 *  Only a superseded sheet has two versions to choose between: a carried-over
 *  page has one, and a NEW sheet has no approved counterpart at all. The
 *  approved page number is already on the row — the planner put it there to say
 *  what this sheet displaces — so choosing it costs nothing but a flag. */
export function canUseApproved(row: CombineEntry): boolean {
  return row.role === "Replaced" && row.replacesPage != null;
}

/**
 * The page a row actually contributes to the built file.
 *
 * THE one place source+page is decided. `toPageRefs` and the row rendering both
 * come through here, so what the operator reads and what the build receives
 * cannot drift — the rule this whole file exists to hold.
 */
export function effectiveRef(row: CombineRow): CombinePageRef | null {
  if (row.useApproved && row.replacesPage != null) {
    return { source: "Approved", page: row.replacesPage };
  }
  if (row.source === "None" || row.page == null) return null;
  return { source: row.source, page: row.page };
}

/** Entries that produce no page: a row with no drawing behind it, an omit for a
 *  sheet the approved set never had. Listed separately — they are things to fix,
 *  not things to place. */
export function notices(plan: CombinePlan | null | undefined): CombineEntry[] {
  return (plan?.entries ?? []).filter((e) => e.source === "None");
}

export interface CombineSummary {
  pages: number;
  carried: number;
  replaced: number;
  added: number;
  removed: number;
  /** Superseded sheets the operator sent back to the approved version. */
  keptApproved: number;
  /** Positions we inferred and nobody has confirmed by moving or accepting. */
  check: number;
  warnings: number;
}

/** The chips above the list. Counted from the ROWS being rendered, never
 *  reported separately: two sources for one number eventually disagree, and the
 *  one the operator is looking at is the true one. */
export function summarize(rows: CombineRow[], noticeCount = 0): CombineSummary {
  const included = rows.filter((r) => r.included);
  return {
    pages: included.length,
    carried: included.filter((r) => r.role === "CarriedOver").length,
    // Counted by what actually happens to the file, for the same reason as
    // `removed` below: a superseded sheet sent back to its approved version is
    // no longer a replacement, whatever the AI1 row said.
    replaced: included.filter((r) => r.role === "Replaced" && !r.useApproved).length,
    added: included.filter((r) => r.role === "Added").length,
    // A removal is counted by what LEAVES the set, so a "keep it anyway" stops
    // counting the moment it is included again.
    removed: rows.filter((r) => r.role === "Removed" && !r.included).length,
    keptApproved: included.filter((r) => r.useApproved).length,
    check: included.filter((r) => r.checkPosition).length,
    warnings: rows.filter((r) => r.warning).length + noticeCount,
  };
}

/** The page list for the build — exactly the included rows, in view order. */
export function toPageRefs(rows: CombineRow[]): CombinePageRef[] {
  const refs: CombinePageRef[] = [];
  for (const row of rows) {
    if (!row.included) continue;
    const ref = effectiveRef(row);
    if (ref) refs.push(ref);
  }
  return refs;
}

/** What to call a row. A page whose sheet number could not be read is named by
 *  where it came from rather than given an invented id. */
export function rowLabel(row: CombineEntry): string {
  if (row.sheetId.trim().length > 0) return row.sheetId;
  const set = row.source === "Approved" ? "approved" : "updated";
  return row.page != null ? `Page ${row.page} (${set} set)` : "Unnumbered sheet";
}

/** The page id as it prints: "A-101.01". */
export function pageIdOf(row: CombineEntry): string {
  return formatPageId(row.sheetId, row.revision);
}

/** The id of the approved sheet a superseding one displaces, as it prints. */
export function approvedPageIdOf(row: CombineEntry): string {
  return formatPageId(row.replacesSheetId ?? "", row.replacesRevision ?? "");
}

/** What this row will print in the built file — the approved sheet's id once
 *  the operator has sent it back. Display reads the same decision the build
 *  does; see effectiveRef. */
export function effectivePageIdOf(row: CombineRow): string {
  return row.useApproved ? approvedPageIdOf(row) : pageIdOf(row);
}

function formatPageId(sheetId: string, revision: string): string {
  const sheet = (sheetId ?? "").trim();
  if (!sheet) return "";
  const minor = /^0?\.?(\d{1,2})$/.exec((revision ?? "").trim())?.[1];
  return minor ? `${sheet}.${minor.padStart(2, "0")}` : sheet;
}

/** Where a row sits, said the way the operator would say it. Used for the
 *  telemetry that tells us the placement rule is wrong. */
export function positionLabel(rows: CombineRow[], id: string): string {
  const included = rows.filter((r) => r.included);
  const at = included.findIndex((r) => r.id === id);
  if (at < 0) return "not in the set";
  if (at === 0) return "first page";
  return `after ${rowLabel(included[at - 1])}`;
}

/** Move a row to a new index in the full (view) order. */
export function moveRow(rows: CombineRow[], from: number, to: number): CombineRow[] {
  if (from === to || from < 0 || to < 0 || from >= rows.length || to >= rows.length) {
    return rows;
  }
  const next = rows.slice();
  const [row] = next.splice(from, 1);
  next.splice(to, 0, row);
  return next;
}

/** In or out of the file. A removal put back is still shown as a removal — the
 *  AI1 says the sheet is omitted, and pretending otherwise on this screen would
 *  hide the disagreement instead of surfacing it. */
export function toggleIncluded(rows: CombineRow[], id: string): CombineRow[] {
  return rows.map((r) => (r.id === id ? { ...r, included: !r.included } : r));
}

/** Keep the approved version of a superseded sheet instead of the new one.
 *
 *  The sheet stays exactly where it is and stays in the file — only which of
 *  the two drawings goes in changes. The row keeps its "Replaced" role so the
 *  screen still says the architect issued a new version and the operator turned
 *  it down; rewriting it to "CarriedOver" would erase the decision, the same
 *  reason a removal put back is still shown as a removal. */
export function toggleUseApproved(rows: CombineRow[], id: string): CombineRow[] {
  return rows.map((r) =>
    r.id === id && canUseApproved(r) ? { ...r, useApproved: !r.useApproved } : r,
  );
}

/** Accept a placement we inferred: the chip goes away, nothing else changes.
 *  Not required before building — it is a way to say "I looked", so the count in
 *  the footer means something. */
export function acceptPosition(rows: CombineRow[], id: string): CombineRow[] {
  return rows.map((r) => (r.id === id ? { ...r, checkPosition: false } : r));
}

/**
 * What the operator changed about our order.
 *
 * The moved rows are the ones NOT in the longest common subsequence of the two
 * orders, which matters: dragging one sheet past three others changes the
 * neighbour of all four, and reporting four moves would teach us that our
 * placement of three sheets we got right was wrong.
 */
export function collectMoves(initial: CombineRow[], current: CombineRow[]): CombineMove[] {
  const moves: CombineMove[] = [];
  const byId = new Map(initial.map((r) => [r.id, r]));

  // A page taken out or put back changes the sequence too, and it is already
  // being reported as what it is — counting it as a move as well would say the
  // operator moved a sheet they only kept.
  const inclusionChanged = new Set(
    current.filter((r) => byId.get(r.id)?.included !== r.included).map((r) => r.id),
  );

  const initialIncluded = initial.filter((r) => r.included).map((r) => r.id);
  const currentIncluded = current.filter((r) => r.included).map((r) => r.id);

  const stayed = longestCommonOrder(initialIncluded, currentIncluded);
  for (const row of current) {
    if (!row.included || stayed.has(row.id) || inclusionChanged.has(row.id)) continue;
    const was = positionLabel(initial, row.id);
    const now = positionLabel(current, row.id);
    if (was !== now) moves.push({ sheet: rowLabel(row), was, now, kind: "position" });
  }

  // Kept in, or taken out, by hand. Both are corrections to what we proposed.
  for (const row of current) {
    if (!inclusionChanged.has(row.id)) continue;
    moves.push({
      sheet: rowLabel(row),
      was: byId.get(row.id)!.included ? "in the set" : "removed from the set",
      now: row.included ? "kept in the set" : "removed from the set",
      kind: "inclusion",
    });
  }

  // Which of the two versions of a superseded sheet goes in. Reported on its
  // own because it is not a correction to where we PUT the sheet — the position
  // was right — it is the operator refusing the architect's new drawing. A
  // sheet that was both moved and sent back reports both, which is accurate:
  // they are two different judgements about it.
  for (const row of current) {
    const before = byId.get(row.id);
    if (!before || before.useApproved === row.useApproved) continue;
    moves.push({
      sheet: rowLabel(row),
      was: describeVersion(before),
      now: describeVersion(row),
      kind: "source",
    });
  }

  return moves;
}

// The version of a sheet a row is carrying, said the way the row says it.
function describeVersion(row: CombineRow): string {
  const id = effectivePageIdOf(row);
  const set = row.useApproved ? "approved" : "updated";
  return id ? `${set} ${id}` : `${set} set`;
}

// The ids that kept their relative order (a longest increasing subsequence over
// the original positions). Everything else is what somebody actually moved.
function longestCommonOrder(initialIds: string[], currentIds: string[]): Set<string> {
  const rank = new Map(initialIds.map((id, i) => [id, i] as const));
  const items = currentIds
    .map((id) => ({ id, rank: rank.get(id) }))
    .filter((x): x is { id: string; rank: number } => x.rank != null);

  // Patience-style LIS with parent links, so the actual set is recoverable.
  const tails: number[] = []; // index into items, per length
  const parent: number[] = new Array(items.length).fill(-1);

  for (let i = 0; i < items.length; i++) {
    let lo = 0;
    let hi = tails.length;
    while (lo < hi) {
      const mid = (lo + hi) >> 1;
      if (items[tails[mid]].rank < items[i].rank) lo = mid + 1;
      else hi = mid;
    }
    parent[i] = lo > 0 ? tails[lo - 1] : -1;
    tails[lo] = i;
  }

  const kept = new Set<string>();
  let cursor = tails.length > 0 ? tails[tails.length - 1] : -1;
  while (cursor >= 0) {
    kept.add(items[cursor].id);
    cursor = parent[cursor];
  }
  return kept;
}

// ---------------------------------------------------------------------------
// The list, grouped for reading
// ---------------------------------------------------------------------------

export type CombineListItem =
  | { kind: "row"; row: CombineRow; index: number }
  | {
      kind: "run";
      key: string;
      ids: string[];
      from: string;
      to: string;
      count: number;
      firstIndex: number;
    };

/** Consecutive untouched pages, folded up.
 *
 *  A real filing is 60 pages of which 9 changed, and a list where the nine are
 *  buried in fifty-one identical rows is a list nobody reads to the end. Runs
 *  shorter than this are not worth hiding — the fold costs a click and saves
 *  nothing. */
const MIN_RUN = 3;

export function groupRows(
  rows: CombineRow[],
  collapse: boolean,
  expanded: ReadonlySet<string>,
): CombineListItem[] {
  const items: CombineListItem[] = [];
  let i = 0;

  while (i < rows.length) {
    const row = rows[i];
    if (!collapse || !isQuiet(row)) {
      items.push({ kind: "row", row, index: i });
      i++;
      continue;
    }

    let j = i;
    while (j < rows.length && isQuiet(rows[j])) j++;
    const run = rows.slice(i, j);

    const key = `run-${run[0].id}`;
    if (run.length < MIN_RUN || expanded.has(key)) {
      run.forEach((r, k) => items.push({ kind: "row", row: r, index: i + k }));
    } else {
      items.push({
        kind: "run",
        key,
        ids: run.map((r) => r.id),
        from: rowLabel(run[0]),
        to: rowLabel(run[run.length - 1]),
        count: run.length,
        firstIndex: i,
      });
    }
    i = j;
  }

  return items;
}

// A row with nothing to say: carried over, where the approved set put it, with
// no warning and nothing inferred. Anything else always shows.
function isQuiet(row: CombineRow): boolean {
  return (
    row.role === "CarriedOver" &&
    row.included &&
    !row.checkPosition &&
    !row.warning
  );
}
